import { Show, createMemo, createSignal, type Component } from "solid-js";
import { PanelLayout, type PanelStatus } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { useWs } from "@/shared/ws";
import { registerPanel } from "@/panel-registry";

interface SchTask {
  TaskName?: string;
  TaskPath?: string;
  State?: string;
  Author?: string;
  Description?: string;
}

interface ListResult<T> {
  rows: T[];
}

type SchActionId = "run" | "enable" | "disable" | "delete";

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

const SchtasksPanel: Component = () => {
  const ws = useWs();
  const confirm = useConfirm();
  const tools = useToolClient();
  const [pathFilter, setPathFilter] = createSignal("");
  const [nameFilter, setNameFilter] = createSignal("");
  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);
  const [detailsJson, setDetailsJson] = createSignal<string | null>(null);
  const [detailsTitle, setDetailsTitle] = createSignal("");

  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const tasksQuery = createToolQuery<ListResult<SchTask>>(() => ({
    tool: "schtasks_list",
    params: { limit: 500 },
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const filteredRows = createMemo<SchTask[]>(() => {
    const rows = tasksQuery.data?.rows ?? [];
    const p = pathFilter().trim().toLowerCase();
    const n = nameFilter().trim().toLowerCase();
    if (!p && !n) return rows;
    return rows.filter((r) => {
      const tp = String(r.TaskPath ?? "").toLowerCase();
      const tn = String(r.TaskName ?? "").toLowerCase();
      if (p && !tp.includes(p)) return false;
      if (n && !tn.includes(n)) return false;
      return true;
    });
  });

  async function runAction(
    row: SchTask,
    actionId: SchActionId,
    label: string,
    level: "info" | "warn" | "danger",
  ): Promise<void> {
    const taskPath = row.TaskPath ?? "";
    const taskName = row.TaskName ?? "";
    if (!taskName) return;
    const ok = await confirm.confirm({
      title: `${label} — ${taskName}`,
      body: `Confirmer "${label}" sur la tache "${taskPath}${taskName}" ?`,
      level,
      confirmLabel: label,
      entityName: level === "danger" ? taskName : "",
    });
    if (!ok) return;
    setActionStatus({ text: `Execution ${label} sur ${taskName}...`, level: "info" });
    try {
      await tools.call(
        "schtasks_action",
        { taskPath, taskName, action: actionId },
        { userConfirmedAt: new Date().toISOString() },
      );
      setActionStatus({ text: `Action ${label} reussie.`, level: "info" });
      await tasksQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  async function viewDetails(row: SchTask): Promise<void> {
    const taskPath = row.TaskPath ?? "";
    const taskName = row.TaskName ?? "";
    if (!taskName) return;
    setDetailsTitle(`${taskPath}${taskName}`);
    setDetailsJson("Chargement...");
    try {
      const result = await tools.call<Record<string, unknown>>("schtasks_get", {
        taskPath,
        taskName,
      });
      const task = (result as { task?: unknown }).task !== undefined ? (result as { task?: unknown }).task : result;
      setDetailsJson(JSON.stringify(task, null, 2));
    } catch (err) {
      setDetailsJson(`Erreur: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  const columns: Column<SchTask>[] = [
    { accessorKey: "TaskName", header: "TaskName" },
    { accessorKey: "TaskPath", header: "TaskPath" },
    { accessorKey: "State", header: "State" },
    { accessorKey: "Author", header: "Author" },
    { accessorKey: "Description", header: "Description" },
  ];

  const toolbar = (
    <>
      <input
        type="text"
        class="panel-filter-input"
        placeholder="\\Microsoft\\Windows"
        spellcheck={false}
        title="Filtre TaskPath"
        value={pathFilter()}
        onInput={(e) => setPathFilter(e.currentTarget.value)}
      />
      <input
        type="text"
        class="panel-filter-input"
        placeholder="Filtre nom..."
        spellcheck={false}
        value={nameFilter()}
        onInput={(e) => setNameFilter(e.currentTarget.value)}
      />
      <button type="button" class="btn btn--xs btn--info" onClick={() => tasksQuery.refetch()}>
        Rafraichir
      </button>
    </>
  );

  const status = (): PanelStatus | undefined => {
    if (actionStatus()) return actionStatus() ?? undefined;
    if (!ws.state.nativeAgentOnline) return { text: "Module natif requis.", level: "warn" };
    if (!ws.state.supportApproved) return { text: "Validation client requise.", level: "warn" };
    if (tasksQuery.isFetching) return { text: "Chargement...", level: "info" };
    if (tasksQuery.error)
      return {
        text: `Erreur: ${tasksQuery.error instanceof Error ? tasksQuery.error.message : String(tasksQuery.error)}`,
        level: "danger",
      };
    const total = tasksQuery.data?.rows.length ?? 0;
    const filtered = filteredRows().length;
    if (total > 0) {
      const suffix = filtered !== total ? ` (filtre sur ${total})` : "";
      return {
        text: `${filtered} resultat${filtered > 1 ? "s" : ""}${suffix}`,
        level: filtered ? "info" : "warn",
      };
    }
    return undefined;
  };

  // référence formatCell pour éviter warning unused
  void formatCell;

  return (
    <>
      <PanelLayout title="Taches planifiees" toolbar={toolbar} status={status()}>
        <DataTable<SchTask>
          rows={filteredRows()}
          columns={columns}
          rowActions={(r) => {
            const state = String(r.State ?? "").toLowerCase();
            const isDisabled = state === "disabled";
            const isRunning = state === "running";
            return (
              <>
                <button type="button" class="btn btn--xs btn--ghost" onClick={() => viewDetails(r)}>
                  Details
                </button>
                <button
                  type="button"
                  class="btn btn--xs btn--ghost"
                  disabled={isRunning}
                  onClick={() => runAction(r, "run", "Run", "info")}
                >
                  Run
                </button>
                <button
                  type="button"
                  class="btn btn--xs btn--warn"
                  disabled={!isDisabled}
                  onClick={() => runAction(r, "enable", "Enable", "warn")}
                >
                  Enable
                </button>
                <button
                  type="button"
                  class="btn btn--xs btn--warn"
                  disabled={isDisabled}
                  onClick={() => runAction(r, "disable", "Disable", "warn")}
                >
                  Disable
                </button>
                <button
                  type="button"
                  class="btn btn--xs btn--danger"
                  onClick={() => runAction(r, "delete", "Delete", "danger")}
                >
                  Delete
                </button>
              </>
            );
          }}
        />
      </PanelLayout>
      <Show when={detailsJson() != null}>
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: "0",
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            "align-items": "center",
            "justify-content": "center",
            "z-index": "1000",
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setDetailsJson(null);
          }}
        >
          <div
            style={{
              background: "#fff",
              color: "#111",
              "max-width": "80vw",
              "max-height": "80vh",
              overflow: "auto",
              padding: "16px",
              "border-radius": "8px",
              "box-shadow": "0 12px 40px rgba(0,0,0,0.35)",
              "min-width": "480px",
            }}
          >
            <div
              style={{
                display: "flex",
                "justify-content": "space-between",
                "align-items": "center",
                "margin-bottom": "8px",
              }}
            >
              <strong>{detailsTitle()}</strong>
              <button type="button" class="btn btn--xs btn--ghost" onClick={() => setDetailsJson(null)}>
                Fermer
              </button>
            </div>
            <pre
              style={{
                "white-space": "pre-wrap",
                "word-break": "break-word",
                "font-family": "Menlo, Consolas, monospace",
                "font-size": "12px",
                background: "#f7f7f9",
                padding: "12px",
                "border-radius": "4px",
                "max-height": "60vh",
                overflow: "auto",
                margin: "0",
              }}
            >
              {detailsJson() ?? ""}
            </pre>
          </div>
        </div>
      </Show>
    </>
  );
};

registerPanel("schtasks", SchtasksPanel);
export default SchtasksPanel;
